import React, { createContext, useContext, useState, useEffect, useReducer, ReactNode } from 'react';
import { loadingPlansService, LoadingPlanResponse, LoadingPlansListResponse, FetchLoadingPlansParams } from '../../services';
import { Part } from '../../services/api/partsService';
import { LoadingPlanStatus } from '../../types/loadingPlan';
import { useAuth } from '../AuthContext';

export interface TransformedMeshAABB {
  min: [number, number, number];
  max: [number, number, number];
}

export interface PartOrientation {
  transform_matrix: number[][];
  transformed_mesh_aabb: TransformedMeshAABB;
  floors_bases?: any[];
  ceilings_bases?: any[];
}

export interface PartOrientationsItem {
  part_index: number;
  part_id: string;
  orientations: PartOrientation[];
}

// The server keeps parts_orientations out of this (it can be megabytes): see loadingPlansService.fetchPartsOrientations.
export interface NestingOutput {
  excluded_parts: ExcludedPart[];
  nested_parts: NestedPart[];
  truck: any;
}

interface ExcludedPart {
  part_id: string;
  part_index?: number;
  reason: string;
}
interface NestedPart {
  part_id: string;
  part_index?: number;
  order_number: number;
  transform_matrix: number[][];
  assigned_loading_layer: number;
  // Index into the part's precomputed stable orientations. Must be echoed back verbatim when
  // re-sending this part as preplaced/locked (see partialRenest.utils.ts) - the engine defaults
  // a missing value to -1, which its own preplaced-part bookkeeping mishandles.
  orientation_index?: number;
  // Parts stacked on a pallet - the engine reports these only nested here, never as their own
  // top-level nested_parts entry.
  child_items?: NestedPart[];
}

export interface LoadingPlan {
  id: number;
  title: string;
  status: LoadingPlanStatus;
  ifcFileUrl: string | null;
  shipDate: string | null;
  trailerLength: number;
  trailerMaxWidth: number;
  trailerMaxWeight: number;
  trailerMaxHeight: number;
  nestingOutput: NestingOutput | null;
  // True once every included part has a placement or exclusion - i.e. no part is still waiting
  // on a windowed/incremental nesting call. Drives the Step 4 "Finish Layout" gate.
  nestingCoverageComplete?: boolean;
  pdfFileStorageId: string | null;
  shareToken: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  includedParts: Part[];
  notIncludedParts: Part[];
  includedPartsCount: string;
}

export interface UpdateLoadingPlanPayload extends Partial<LoadingPlan> {
  partIds: number[];
}

// Display type for UI components
export interface LoadingPlanDisplay {
  id: number;
  name: string;
  status: LoadingPlanStatus;
  created: string;
  createdShort: string;
  shipDate: string;
  trailer: string;
  parts: number;
  weight: string;
  tags: string[];
  actions: string[];
  utilization?: string;
  layers?: string;
  includedParts: Part[];
  includedPartsCount: string;
}

// Helper functions to format LoadingPlan data for display
export const formatLoadingPlanForDisplay = (plan: LoadingPlan): LoadingPlanDisplay => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  };

  return {
    id: plan.id,
    name: plan.title,
    status: plan.status,
    created: `Created ${formatDate(plan.createdAt)}`,
    createdShort: formatDate(plan.createdAt),
    shipDate: formatDate(plan.shipDate),
    trailer: plan.trailerLength ? `${plan.trailerLength} ft` : '-',
    parts: Number(plan.includedPartsCount),
    weight: '0',
    tags: [] as string[],
    actions: getActionsByStatus(plan.status),
    utilization: undefined,
    layers: undefined,
    includedParts: plan.includedParts,
    includedPartsCount: plan.includedPartsCount,
  };
};

const getActionsByStatus = (status: LoadingPlanStatus): string[] => {
  switch (status) {
    case LoadingPlanStatus.DRAFT:
    case LoadingPlanStatus.IN_PROGRESS:
      return ['CONTINUE'];
    case LoadingPlanStatus.READY:
      return ['EDIT', 'PUBLISH'];
    case LoadingPlanStatus.PUBLISHED:
      return ['VIEW'];
    default:
      return [];
  }
};

interface LoadingPlanContextType {
  loadingPlans: LoadingPlan[];
  loading: boolean;
  error: string | null;
  refetchLoadingPlans: (params?: FetchLoadingPlansParams) => Promise<LoadingPlansListResponse | undefined>;
  currentLoadingPlan: LoadingPlanResponse;
  setCurrentLoadingPlan: (plan: LoadingPlanResponse) => void;
  clearCurrentLoadingPlan: () => void;
  updateCurrentLoadingPlan: (updates: Partial<UpdateLoadingPlanPayload>) => void;
}

const LoadingPlanContext = createContext<LoadingPlanContextType | undefined>(undefined);

export const currentLoadingPlanDefaultState: LoadingPlan = {
  id: 0,
  title: '',
  status: LoadingPlanStatus.DRAFT,
  ifcFileUrl: null,
  shipDate: null,
  trailerLength: 48,
  trailerMaxWidth: 7,
  trailerMaxWeight: 45000,
  trailerMaxHeight: 7,
  nestingOutput: null,
  nestingCoverageComplete: false,
  pdfFileStorageId: null,
  shareToken: null,
  createdBy: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  includedParts: [],
  notIncludedParts: [],
  includedPartsCount: '0',
};

// Reducer action types
type LoadingPlanAction =
  | { type: 'SET_LOADING_PLAN'; payload: LoadingPlan }
  | { type: 'UPDATE_LOADING_PLAN'; payload: Partial<LoadingPlan> }
  | { type: 'CLEAR_LOADING_PLAN' };

// Reducer function
const loadingPlanReducer = (state: LoadingPlan, action: LoadingPlanAction): LoadingPlan => {
  switch (action.type) {
    case 'SET_LOADING_PLAN':
      return action.payload;
    case 'UPDATE_LOADING_PLAN':
      return { ...state, ...action.payload };
    case 'CLEAR_LOADING_PLAN':
      return currentLoadingPlanDefaultState;
    default:
      return state;
  }
};

export const LoadingPlanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loadingPlans, setLoadingPlans] = useState<LoadingPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentLoadingPlan, dispatch] = useReducer(loadingPlanReducer, currentLoadingPlanDefaultState);

  const { isAuthenticated } = useAuth();

  const setCurrentLoadingPlan = (plan: LoadingPlan) => {
    dispatch({ type: 'SET_LOADING_PLAN', payload: plan });
  };

  const updateCurrentLoadingPlan = (updates: Partial<LoadingPlan>) => {
    dispatch({ type: 'UPDATE_LOADING_PLAN', payload: updates });
  };

  const clearCurrentLoadingPlan = () => {
    dispatch({ type: 'CLEAR_LOADING_PLAN' });
  };

  // Fetch loading plans from backend
  const fetchLoadingPlans = async (params?: FetchLoadingPlansParams): Promise<LoadingPlansListResponse | undefined> => {
    try {
      setLoading(true);
      setError(null);
      const response = await loadingPlansService.fetchLoadingPlans(params);
      setLoadingPlans(response.loadingPlans);
      return response;
    } catch (err) {
      console.error('Failed to fetch loading plans:', err);
      setError('Failed to load loading plans. Please try again.');
      setLoadingPlans([]);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchLoadingPlans();
    }
  }, [isAuthenticated]);

  return (
    <LoadingPlanContext.Provider value={{
      loadingPlans,
      loading,
      error,
      refetchLoadingPlans: fetchLoadingPlans,
      currentLoadingPlan,
      setCurrentLoadingPlan,
      clearCurrentLoadingPlan,
      updateCurrentLoadingPlan
    }}>
      {children}
    </LoadingPlanContext.Provider>
  );
};

export const useLoadingPlan = () => {
  const context = useContext(LoadingPlanContext);
  if (context === undefined) {
    throw new Error('useLoadingPlan must be used within a LoadingPlanProvider');
  }
  return context;
};